// Framework prompt template used when the AI assigns points to questionnaire
// answers. Placeholders are substituted at runtime by renderPointScoringPrompt.
// Available placeholders:
//   {{guidelines}}   – job-specific scoring guidance (system + template prompt)
//   {{totalScore}}   – overall total score budget (number)
//   {{pageBudgets}}  – page-budget info block (already includes the rule), or ''
//   {{questions}}    – formatted list of questions with options
export const DEFAULT_POINT_SCORING_PROMPT = `Du bist ein HR-Bewertungsexperte. Erstelle ein Punktesystem für einen Fragebogen.

Bewertungs-Richtlinien: {{guidelines}}

Gesamtpunktzahl: {{totalScore}}
Halte die gesamte Punktesumme aller Fragen genau bei {{totalScore}}.{{pageBudgets}}

Fragen:
{{questions}}

Für jede Frage:
1) Vergib "maxScore" (Maximalpunkte der Frage)
2) Falls die Frage Optionen hat (single-choice, multiple-choice, matrix, combobox, ranking): Vergib "optionScores" als Array mit Punkten pro Option. JEDE Option MUSS einen EINZIGARTIGEN Wert > 0 bekommen, abgestuft nach Qualität — KEINE zwei Optionen einer Frage dürfen den gleichen Punktwert haben (z. B. [5,4,3,2,1] ist erlaubt, [5,5,3,2,1] ist VERBOTEN). Setze maxScore notfalls hoch genug, dass alle Optionen unterschiedliche ganzzahlige Werte erhalten können.
3) Falls keine Optionen vorhanden sind (text, file-upload): setze "optionScores" auf null

Scoring-Regeln pro Typ:
- single-choice: Bewerber wählt eine Option. Die beste Option bekommt maxScore, schlechtere weniger.
- multiple-choice: Bewerber wählt mehrere Optionen. Punkte addieren sich.
- matrix: Bewerber wählt pro Zeile eine Spaltenoption. Punkte addieren sich über alle Zeilen. Einzelne Optionsscores sollten so gewählt werden, dass die Summe über alle Zeilen den maxScore erreichen kann.
- combobox: Wie matrix – pro Eintrag wird eine Dropdown-Option gewählt, Punkte addieren sich.
- ranking: Bewerber ordnet Optionen nach Priorität. Pro Option die korrekt platziert ist, gibt es deren Punkte.

Wichtig:
- Alle Werte müssen ganze Zahlen >= 0 sein
- Innerhalb einer Frage müssen ALLE optionScores paarweise unterschiedlich sein (keine Duplikate)
- Bei single-choice: maximal eine Option darf maxScore erreichen
- Bei matrix/combobox: optionScores pro Spalte × Zeilenanzahl ≈ maxScore
- Summe aller maxScore-Werte muss genau {{totalScore}} ergeben
- Fragen mit ⭐ WICHTIG sollen deutlich höhere maxScore-Werte erhalten (ca. 1.5x so viel wie vergleichbare Fragen)

Antworte als JSON-Array. Jedes Element hat:
- "questionId": ID der Frage
- "maxScore": Maximale Punktzahl für diese Frage
- "optionScores": Array mit Punkten pro Option (bei single-choice, multiple-choice, matrix, combobox, ranking), sonst null. MUSS für jeden Typ mit Optionen befüllt sein!`;

export const POINT_SCORING_PROMPT_PLACEHOLDERS = [
  { token: '{{guidelines}}', description: 'Job-spezifische Bewertungs-Richtlinien (Analyse + Scoring-Erweiterung).' },
  { token: '{{totalScore}}', description: 'Gesamtpunktzahl des Fragebogens.' },
  { token: '{{pageBudgets}}', description: 'Seitenbudget-Block (wird automatisch leer gelassen, wenn keine Seitenbudgets gesetzt sind).' },
  { token: '{{questions}}', description: 'Formatierte Liste aller Fragen mit Optionen.' }
];

const GEMINI_STATUS_MEANINGS = {
  400: 'Ungültige Anfrage (Bad Request) – Prompt oder Schema fehlerhaft',
  401: 'Nicht authentifiziert (Unauthorized) – API-Schlüssel fehlt oder ungültig',
  403: 'Zugriff verweigert (Forbidden) – Schlüssel ohne Berechtigung oder Abrechnung deaktiviert',
  404: 'Nicht gefunden (Not Found) – Modellname oder Endpoint falsch',
  408: 'Zeitüberschreitung (Request Timeout)',
  429: 'Zu viele Anfragen (Rate Limit) – kurz warten und erneut versuchen',
  500: 'Interner Server-Fehler bei Google (Internal Server Error)',
  502: 'Bad Gateway – Google-Backend nicht erreichbar',
  503: 'Dienst überlastet (Service Unavailable) – Modell vorübergehend ausgelastet, in 1–2 Minuten erneut versuchen',
  504: 'Gateway-Timeout – Google-Backend antwortet nicht'
};

// Dieselben Faelle in Badge-Laenge. Zwei Leser: das Tagesprotokoll in den
// Einstellungen und die Fortschrittsanzeige waehrend einer Bewertung.
export const GEMINI_STATUS_SHORT = {
  200: 'OK',
  400: 'Anfrage fehlerhaft',
  401: 'Schlüssel ungültig',
  403: 'Zugriff verweigert',
  404: 'Modell unbekannt',
  408: 'Zeitüberschreitung',
  429: 'Kontingent erschöpft',
  500: 'Fehler bei Google',
  502: 'Google nicht erreichbar',
  503: 'Modell überlastet',
  504: 'Zeitüberschreitung',
  timeout: 'Zeitüberschreitung',
  netzwerk: 'Netzwerkfehler'
};

export const describeGeminiStatusShort = (key) =>
  GEMINI_STATUS_SHORT[key] || GEMINI_STATUS_SHORT[Number(key)] || String(key);

export const describeGeminiApiError = (status, statusText = '') => {
  const code = Number(status);
  const meaning = GEMINI_STATUS_MEANINGS[code] || (statusText ? String(statusText) : 'Unbekannter Fehler');
  return Number.isFinite(code) ? `${code} – ${meaning}` : meaning;
};

export const renderPointScoringPrompt = (template, vars = {}) => {
  const t = (typeof template === 'string' && template.trim()) ? template : DEFAULT_POINT_SCORING_PROMPT;
  const guidelines = vars.guidelines ?? '';
  const totalScore = vars.totalScore ?? '';
  const pageBudgets = vars.pageBudgets ?? '';
  const questions = vars.questions ?? '';
  return t
    .replace(/\{\{guidelines\}\}/g, String(guidelines))
    .replace(/\{\{totalScore\}\}/g, String(totalScore))
    .replace(/\{\{pageBudgets\}\}/g, String(pageBudgets))
    .replace(/\{\{questions\}\}/g, String(questions));
};
